/**
 * The in-place top-up inside a modal (TMMAPP-0118 C11, DLG-005; the import modal's discard panel is
 * the same pattern): the host renders an empty panel host after its own title, content and actions,
 * hides those three while `swapped`, and points its `aria-labelledby` at `TOP_UP_TITLE_ID`. The
 * wallet prompt portals the top-up form into the panel host and reports `swapped` while it shows.
 *
 * The host's close (backdrop, Esc) goes through `requestHostClose` (TMMAPP-0119 item 16): while the
 * top-up shows, it is the top-up's own Cancel, so a charge or a card update in flight holds it and
 * an edited amount asks "Discard this top-up?" first; otherwise the host closes.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

export const TOP_UP_TITLE_ID = 'ai-wallet-top-up-title';

export interface TopUpSwap {
  /** The host's panel host; `null` while it mounts. */
  panelHost: HTMLElement | null;
  /** Must be a stable function (a state setter). */
  onSwap: (swapped: boolean) => void;
  /** The panel title's id, which the host's `aria-labelledby` names while swapped. */
  titleId: string;
  /** The showing top-up registers its close request here (null when it goes); stable. */
  onCloseRequest: (request: (() => void) | null) => void;
}

/**
 * The host-close half of the swap, for a host that builds its `TopUpSwap` itself (the import
 * modal): the top-up registers through `onCloseRequest`, and the host's close goes through
 * `requestHostClose`.
 */
export function useTopUpCloseRequest(): {
  onCloseRequest: (request: (() => void) | null) => void;
  requestHostClose: (close: () => void) => void;
} {
  const request = useRef<(() => void) | null>(null);
  const onCloseRequest = useCallback((next: (() => void) | null) => {
    request.current = next;
  }, []);
  const requestHostClose = useCallback((close: () => void) => {
    const panelClose = request.current;
    if (panelClose !== null) panelClose();
    else close();
  }, []);
  return { onCloseRequest, requestHostClose };
}

export function useTopUpSwap(): {
  swap: TopUpSwap;
  swapped: boolean;
  attachPanelHost: (element: HTMLElement | null) => void;
  /** The host dialog's `onClose` goes through this (item 16). */
  requestHostClose: (close: () => void) => void;
} {
  const [panelHost, setPanelHost] = useState<HTMLElement | null>(null);
  const [swapped, setSwapped] = useState(false);
  const { onCloseRequest, requestHostClose } = useTopUpCloseRequest();
  const swap = useMemo(
    () => ({ panelHost, onSwap: setSwapped, titleId: TOP_UP_TITLE_ID, onCloseRequest }),
    [panelHost, onCloseRequest],
  );
  return { swap, swapped, attachPanelHost: setPanelHost, requestHostClose };
}
